import * as React from 'react';
import { connect } from 'react-redux';
import { provideIntlService, provideLocalizationService, registerForLocalization, registerForIntl } from '@progress/kendo-react-intl';
import { Skeleton } from '@progress/kendo-react-indicators';
import { Field } from '@progress/kendo-react-form';
import { OverviewPage, StackLayout, Text, TextArea } from '../../../ui';
import FormDialog from '../../../ui/components/application/form-dialog';
import { DateTimeFormatter, InlineBadge, ValueItem } from '../../shared/components';
import { getGeneralCompetencyStatusThemeColor } from '../../../resources/generalCompetencyStatus';
import { COMMENT_LENGTH_LIMITS } from '../../../resources/commentLengthLimits';
import {
  loadClaimReviewOverviewPage,
  approveClaim,
  openRejectDialog,
  closeRejectDialog,
  rejectClaim,
  cleanupClaimReviewOverviewPage
} from '../actions/claim-review-overview-page-actions';
import { withRouter } from '../../../components/withRouter';
import { Button } from '@progress/kendo-react-buttons';

class ClaimReviewOverviewPage extends React.PureComponent {

  constructor() {
    super();
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
    this.handleApprove = this.handleApprove.bind(this);
    this.handleOpenRejectDialog = this.handleOpenRejectDialog.bind(this);
    this.handleCloseRejectDialog = this.handleCloseRejectDialog.bind(this);
    this.handleReject = this.handleReject.bind(this);
    this.handleValidateRejectDialog = this.handleValidateRejectDialog.bind(this);
  }

  async componentDidMount() {
    const { claimId } = this.props.params;
    if (claimId) {
      await this.props.loadClaimReviewOverviewPage(claimId);
    }
  }

  componentWillUnmount() {
    this.props.cleanupClaimReviewOverviewPage();
  }

  render() {
    const { claim, loading, pdfObjectUrl, isRejectDialogVisible, rejectValidationResult } = this.props;
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    const isPendingReview = claim?.statusCode === 'PendingReview';
    return (
      <OverviewPage
        title={loading
          ? <Skeleton shape='text' style={{ width: 300, height: 28 }}/>
          : localizationService.toLanguageString('generalCompetencyClaimReview.overviewTitle')
        }
        breadcrumbItems={[
          {
            id: 'claims',
            text: localizationService.toLanguageString('generalCompetencyClaimReview.listTitle')
          },
          {
            id: 'claim',
            text: localizationService.toLanguageString('generalCompetencyClaimReview.overviewTitle'),
            disabled: true
          }
        ]}
        onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
        onBackClick={this.handleBackClick}
        headerActions={
          isPendingReview &&
          <>
            <Button themeColor='primary' onClick={this.handleApprove}>
              {localizationService.toLanguageString('generalCompetencyClaimReview.approve')}
            </Button>
            <Button onClick={this.handleOpenRejectDialog}>
              {localizationService.toLanguageString('generalCompetencyClaimReview.reject')}
            </Button>
          </>
        }
        summary={
          <StackLayout rowGap='8px'>
            <ValueItem
              label={localizationService.toLanguageString('generalCompetencyClaimReview.studentName')}
              labelWidth='150px'
              loading={loading}
            >
              {claim?.studentName}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('generalCompetencyClaimReview.title')}
              labelWidth='150px'
              loading={loading}
            >
              {claim?.title}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('generalCompetencyClaimReview.organizer')}
              labelWidth='150px'
              loading={loading}
            >
              {claim?.organizer}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('generalCompetencyClaimReview.occurredAt')}
              labelWidth='150px'
              loading={loading}
            >
              {claim?.occurredAt ? DateTimeFormatter(claim.occurredAt, intlService) : null}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('generalCompetencyClaimReview.minutes')}
              labelWidth='150px'
              loading={loading}
            >
              {claim?.minutes}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('generalCompetencyClaimReview.description')}
              labelWidth='150px'
              loading={loading}
            >
              {claim?.description}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('generalCompetencyClaimReview.status')}
              labelWidth='150px'
              loading={loading}
            >
              {claim?.status &&
                <InlineBadge themeColor={getGeneralCompetencyStatusThemeColor(claim.statusCode)}>
                  {claim.status}
                </InlineBadge>
              }
            </ValueItem>
            {claim?.reviewComment &&
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyClaimReview.reviewComment')}
                labelWidth='150px'
                loading={loading}
              >
                {claim.reviewComment}
              </ValueItem>
            }
            {claim?.reviewedAt &&
              <ValueItem
                label={localizationService.toLanguageString('generalCompetencyClaimReview.reviewedAt')}
                labelWidth='150px'
                loading={loading}
              >
                {DateTimeFormatter(claim.reviewedAt, intlService)}
              </ValueItem>
            }
            <ValueItem
              label={localizationService.toLanguageString('generalCompetencyClaimReview.createdOn')}
              labelWidth='150px'
              loading={loading}
            >
              {claim?.createdOn ? DateTimeFormatter(claim.createdOn, intlService) : null}
            </ValueItem>
          </StackLayout>
        }
      >
        {!loading && pdfObjectUrl &&
          <iframe
            src={pdfObjectUrl}
            title={localizationService.toLanguageString('generalCompetencyClaimReview.attachment')}
            width='100%'
            height='800'
          />
        }
        {!loading && !pdfObjectUrl && claim &&
          <Text variant='subtitle1'>
            {localizationService.toLanguageString('generalCompetencyClaimReview.noAttachment')}
          </Text>
        }
        {isRejectDialogVisible &&
          <FormDialog
            title={localizationService.toLanguageString('generalCompetencyClaimReview.rejectDialogTitle')}
            onSubmit={this.handleReject}
            onCancel={this.handleCloseRejectDialog}
            validator={this.handleValidateRejectDialog}
            validationResult={rejectValidationResult}
            confirmButtonText={localizationService.toLanguageString('generalCompetencyClaimReview.reject')}
            closeButtonText={localizationService.toLanguageString('buttons.cancel')}
            width={'640px'}
            render={() => (
              <StackLayout>
                <Field
                  name='reviewComment'
                  label={localizationService.toLanguageString('generalCompetencyClaimReview.reviewComment')}
                  component={TextArea}
                  rows={5}
                  autoSize={true}
                  characterLimit={COMMENT_LENGTH_LIMITS.RETURN_REASON}
                />
              </StackLayout>
            )}
          />
        }
      </OverviewPage>
    );
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'claims') {
      this.props.navigate('/general_competency_claims');
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleApprove() {
    this.props.approveClaim();
  }

  handleOpenRejectDialog() {
    this.props.openRejectDialog();
  }

  handleCloseRejectDialog() {
    this.props.closeRejectDialog();
  }

  handleReject(values) {
    this.props.rejectClaim(values.reviewComment);
  }

  handleValidateRejectDialog(values, localizationService, validationResult, modified) {
    const maxCommentLength = COMMENT_LENGTH_LIMITS.RETURN_REASON;
    const errors = {};
    if (!values.reviewComment || !values.reviewComment.trim()) {
      errors.reviewComment = localizationService.toLanguageString('validation.required');
    } else if (values.reviewComment.length > maxCommentLength) {
      errors.reviewComment = `${localizationService.toLanguageString('validation.exceedsMaximumLength')} (${maxCommentLength})`;
    }
    if (!modified.reviewComment && validationResult?.errors?.reviewComment) {
      errors.reviewComment = validationResult.errors.reviewComment;
    }
    return errors;
  }
}

registerForLocalization(ClaimReviewOverviewPage);
registerForIntl(ClaimReviewOverviewPage);

const mapStateToProps = state => ({
  claim: state.claimReviewOverviewPage.claim,
  loading: state.claimReviewOverviewPage.loading,
  pdfObjectUrl: state.claimReviewOverviewPage.pdfObjectUrl,
  isRejectDialogVisible: state.claimReviewOverviewPage.isRejectDialogVisible,
  rejectValidationResult: state.claimReviewOverviewPage.rejectValidationResult
});

const mapDispatchToProps = dispatch => ({
  loadClaimReviewOverviewPage: (claimId) => dispatch(loadClaimReviewOverviewPage(claimId)),
  approveClaim: () => dispatch(approveClaim()),
  openRejectDialog: () => dispatch(openRejectDialog()),
  closeRejectDialog: () => dispatch(closeRejectDialog()),
  rejectClaim: (reviewComment) => dispatch(rejectClaim(reviewComment)),
  cleanupClaimReviewOverviewPage: () => dispatch(cleanupClaimReviewOverviewPage())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(ClaimReviewOverviewPage));
